import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Player, DraftTeam, DraftState } from '../../types';
import PickOrderDisplay from './PickOrderDisplay';
import PlayerPool from './PlayerPool'; 
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {DraftPageContainer, DraftHeader, Title, DraftTimerContainer, DraftTimerLabel, DraftTimerDisplay, DraftContent, TeamsSection, TeamCardContainer, TeamHeader, PlayerList, PlayerListItem, PlayerInfoOnCard, PlayerNameOnCard, PlayerRolesOnCard, PlayerEloOnCard} from '../../styles';
import { usePlayers } from '../../context/PlayerContext';
import { useAuth } from '../Common/AuthContext';
import { useDivision } from '../../context/DivisionContext';
import { compareRanks, createOpGgUrl, rankTierToShortName, getFirebasePrefix, convertRankToElo, isPlayerCaptain } from '../../utils';
import { ceil } from 'lodash';

const DRAFT_PICK_TIME_LIMIT_IN_MS = 40 * 60 * 1000;

const calculatePickIndex = (pickNumber: number, numCaptains: number, captainIndex: number): number => {
  if (pickNumber % 2 === 1) {
    return (pickNumber - 1) * numCaptains + captainIndex;
  } else {
    return (pickNumber - 1) * numCaptains + (numCaptains - 1 - captainIndex);
  }
}

const initializeDraft = (allPlayers: Player[], division: string): DraftState => {
  if (!allPlayers) return emptyDraftState();
  let captains = allPlayers.filter(p => isPlayerCaptain(p, division)).sort((a, b) => compareRanks(b, a));
  const numCaptains = captains.length;
  if (numCaptains * 5 > allPlayers.length) {
    // there are more captains than players, cut the bottom X captains
    const diff = numCaptains * 5 - allPlayers.length;
    const numCaptainsToRemove = ceil(diff / 5);

    for (let i = 0; i < numCaptainsToRemove; i++){
      captains.pop();
    }
  }
  // Reverse the order so lowest ranked player picks first.
  captains = captains.reverse();

  const availablePlayers = allPlayers.filter(p => !isPlayerCaptain(p, division));
  const allPlayersSorted = [...allPlayers].sort((a, b) => compareRanks(b, a)).reverse();

  const teams: DraftTeam[] = captains.map((captain, index) => ({
    id: index + 1,
    name: `Team ${captain.name}`,
    captainId: captain.id,
    players: [captain],
    wins: 0,
    losses: 0,
    gameWins: 0,
    gameLosses: 0
  }));

  const numRounds = 5; // To get to 5 players total
  const numTeams = teams.length;
  const pickOrder: (number|string)[] = [];
  for (let i = 0; i < numRounds; i++) {
    const roundOrder = Array.from({ length: numTeams }, (_, j) => teams[j].id);
    if ((i + 1) % 2 === 0) { // Snake draft reverse order for even rounds
      roundOrder.reverse();
    }
    pickOrder.push(...roundOrder);
  }

  let playerSkipSlot: {[playerId: number]: number} = {};
  
  allPlayersSorted.forEach((player, index) => {
    const overallRank = index; // The pick number to be forfeited
    if (isPlayerCaptain(player, division) && pickOrder[overallRank] !== undefined) {
        playerSkipSlot[player.id] = index / allPlayers.length;
      const matchedTeam = teams.find(t => t.captainId === player.id);
      player.teamId = matchedTeam ? matchedTeam.id : null;
    }
  });
  
  captains.forEach((captain) => {
    if (playerSkipSlot[captain.id] !== undefined) {
        const captainPercent = playerSkipSlot[captain.id];
        if (captainPercent <= 0.2) {
          // skip the fifth round pick
          pickOrder[calculatePickIndex(5, numTeams, captain.teamId!) - 1] = captain.name;
        }
        else if (captainPercent <= 0.4) {
          // skip the fourth round pick
          pickOrder[calculatePickIndex(4, numTeams, captain.teamId!) + 1] = captain.name;
        }
        else if (captainPercent <= 0.6) {
          // skip the third round pick
          pickOrder[calculatePickIndex(3, numTeams, captain.teamId!) - 1] = captain.name;
        }
        else if (captainPercent <= 0.8) {
          // skip the second round pick
          pickOrder[calculatePickIndex(2, numTeams, captain.teamId!) + 1] = captain.name;
        }
        else if (captainPercent <= 1) {
          // skip the first round pick
          pickOrder[calculatePickIndex(1, numTeams, captain.teamId!) - 1] = captain.name;
        }
    }
  });

  let currentPickIndex = 0;
  // Update skipped picks
  while (typeof(pickOrder[currentPickIndex]) === 'string' && currentPickIndex < pickOrder.length) {
    currentPickIndex++;
  }

  const prefix = getFirebasePrefix();
  return { teams, pickOrder, availablePlayers, completedPicks: {}, currentPickIndex, draftId: `${prefix}_${division ?? "master"}` };
};

const emptyDraftState = (): DraftState => {
  return {
    teams: [],
    pickOrder: [],
    availablePlayers: [],
    completedPicks: {},
    currentPickIndex: 0,
    draftId: '',
  };
}

const DraftPage: React.FC = () => {
  const navigate = useNavigate();
  const { division } = useDivision();
  const { players: allPlayers } = usePlayers();
  const { captainTeamId, currentUser, isAdmin, authDivision } = useAuth();

  const initialDraftState = initializeDraft(allPlayers, division);
  const { teams, pickOrder } = initialDraftState;

  const [, setHash] = useState(window.location.hash);
  useEffect(() => {
    const handleHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const prefix = getFirebasePrefix();
  const [draftState, setDraftState] = useState<DraftState>(emptyDraftState);
  const [draftDocRef, setDraftDocRef] = useState(doc(db, 'drafts', `${prefix}_${division}`));
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isSpectator, setIsSpectator] = useState(sessionStorage.getItem('isSpectator') === 'true');
  const [isLoading, setIsLoading] = useState(true);

  const activePickOrder = draftState.pickOrder && draftState.pickOrder.length > 0 ? draftState.pickOrder : pickOrder;
  const isDraftComplete = draftState.currentPickIndex >= activePickOrder.length;
  const currentTeamIdPicking = !isDraftComplete ? activePickOrder[draftState.currentPickIndex] : null;

  //// BEGIN AUTH ////
  useEffect(() => {
    const spectatorStatus = sessionStorage.getItem('isSpectator') === 'true';
    setIsSpectator(spectatorStatus);
    // If after checking, user is NOT logged in AND is NOT a spectator, redirect them
    if (!captainTeamId && !spectatorStatus && !isAdmin && !isLoading) {
      navigate('/draft-access');
    }
    setLoadingAuth(false);
  }, [captainTeamId, currentUser, isAdmin, isLoading, navigate, setIsSpectator, setLoadingAuth]);
  //// END AUTH ////

  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    if (isDraftComplete || !draftState.pickEndsAt) {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      const difference = draftState.pickEndsAt! - Date.now();
      return Math.max(0, Math.floor(difference / 1000));
    };

    // Update immediately
    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [draftState.pickEndsAt, isDraftComplete]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    setDraftDocRef(doc(db, 'drafts', `${prefix}_${division}`));
  }, [division, prefix]);

  useEffect(() => {
    async function maybeInitData() {
      // Draft hasn't been initialized yet, update with initialize draft state
      const currentData = await getDoc(draftDocRef);
      if (currentData.exists() && Object.keys(currentData.data()).length <= 1 && allPlayers.length > 0) {
        const initialDraftState = initializeDraft(allPlayers, division);
        // --- Atomically write the entire update back to Firestore ---
        if (!isSpectator && isAdmin) {
          await updateDoc(draftDocRef, {...draftState, ...initialDraftState});
        }
      }
    }

    maybeInitData();

    // onSnapshot listens for any changes to the document
    const unsubscribe = onSnapshot(draftDocRef, async (docSnap) => {
      setIsLoading(false);
      if (docSnap.exists()) {
        const data = docSnap.data() as DraftState;
        if (data.currentPickIndex === draftState.currentPickIndex 
            && data.draftId === draftState.draftId 
            && Object.keys(data).length === Object.keys(draftState).length) {
          return;
        }
        setDraftState(data);
      }
    });

    // Clean up the listener when the component unmounts
    return () => unsubscribe();
  }, [allPlayers, isAdmin, isSpectator, division, draftDocRef, draftState, draftState.currentPickIndex, draftState.draftId, setDraftDocRef]);

  const canDraftNow = (
    !isSpectator &&
    (!!captainTeamId && division === authDivision) &&
    Object.keys(draftState).length > 0 &&
    (draftState?.pickOrder ?
      Number(draftState?.pickOrder[draftState.currentPickIndex]) === Number(captainTeamId) : false)
  ) || isAdmin;

  const handleDraftPlayer = useCallback(async (player: Player) => {
    if (!draftState) return;
    
    if (!canDraftNow) {
      console.error("Draft failed: You do not have permission to draft now.");
      return;
    }

    const { currentPickIndex, teams, completedPicks, availablePlayers, pickOrder } = draftState;
    const currentTeamIdPicking = pickOrder[currentPickIndex];

    if (currentTeamIdPicking === null) return;

    // --- Create the next state object ---
    const newTeams = teams.map(team => 
        team.id === currentTeamIdPicking ? { ...team, players: [...team.players!, player] } : team
    );
    const newAvailablePlayers = availablePlayers.filter(p => p.id !== player.id);
    const newCompletedPicks = { ...completedPicks, [currentPickIndex]: player.id };

    const numTeams = teams.length;
    let newPickOrder = [...pickOrder];

    let nextPickIndex = currentPickIndex + 1;
    // Update skipped picks
    while (nextPickIndex < newPickOrder.length && typeof newPickOrder[nextPickIndex] === "string") {
      nextPickIndex++;
    }

    const currentRound = Math.floor(currentPickIndex / numTeams) + 1;
    const nextRound = Math.floor(nextPickIndex / numTeams) + 1;

    if (nextRound > currentRound && nextRound <= 5) {
      const captains = allPlayers.filter(p => isPlayerCaptain(p, division)).sort((a, b) => compareRanks(b, a));
      const captainsReversed = [...captains].reverse();
      const allPlayersSorted = [...allPlayers].sort((a, b) => compareRanks(b, a)).reverse();

      const playerSkipSlot: { [playerId: number]: number } = {};

      const firstHalfPlayers = allPlayersSorted.reverse().slice(0, allPlayers.length / 2);

      firstHalfPlayers.reverse().forEach((player, index) => {
        if (isPlayerCaptain(player, division)) {
          playerSkipSlot[player.id] = index / (allPlayers.length / 2);
        }
      });

      const teamElos = newTeams.map(team => {
        const totalElo = team.players!.reduce((sum, p) => {
          if (isPlayerCaptain(p, division)) {
            const captainPercent = playerSkipSlot[p.id];
            if (captainPercent !== undefined) {
              let forcedRound = 1;
              if (captainPercent <= 0.2) forcedRound = 5;
              else if (captainPercent <= 0.4) forcedRound = 4;
              else if (captainPercent <= 0.6) forcedRound = 3;
              else if (captainPercent <= 0.8) forcedRound = 2;
              else if (captainPercent <= 1.0) forcedRound = 1;

              if (forcedRound > currentRound) {
                return sum;
              }
            }
          }
          const maxElo = p.elo !== undefined ? p.elo : Math.max(
            convertRankToElo(p.peakRankTier, p.peakRankDivision),
            convertRankToElo(p.soloRankTier, p.soloRankDivision),
            convertRankToElo(p.flexRankTier, p.flexRankDivision)
          );
          return sum + maxElo;
        }, 0);
        return { id: team.id, elo: totalElo };
      });

      teamElos.sort((a, b) => {
        if (a.elo === b.elo) {
          return a.id - b.id;
        }
        return a.elo - b.elo;
      });

      const sortedTeamIds = teamElos.map(t => t.id);

      for (let r = currentRound + 1; r <= 5; r++) {
        const startIdx = (r - 1) * numTeams;
        let roundOrder = [...sortedTeamIds];

        // Snake prediction: alternate order for future rounds
        if ((r - currentRound) % 2 === 0) {
          roundOrder.reverse();
        }

        for (let j = 0; j < numTeams; j++) {
          newPickOrder[startIdx + j] = roundOrder[j];
        }
      }

      captainsReversed.forEach((captain) => {
        const captainPercent = playerSkipSlot[captain.id];
        if (captainPercent !== undefined) {
          let forcedRound = 0;
          if (captainPercent <= 0.2) forcedRound = 5;
          else if (captainPercent <= 0.4) forcedRound = 4;
          else if (captainPercent <= 0.6) forcedRound = 3;
          else if (captainPercent <= 0.8) forcedRound = 2;
          else if (captainPercent <= 1.0) forcedRound = 1;

          if (forcedRound > currentRound) {
            const startIdx = (forcedRound - 1) * numTeams;
            for (let j = 0; j < numTeams; j++) {
              if (newPickOrder[startIdx + j] === captain.teamId) {
                newPickOrder[startIdx + j] = captain.name;
                break;
              }
            }
          }
        }
      });

      nextPickIndex = currentPickIndex + 1;
      // Update skipped picks
      while (nextPickIndex < newPickOrder.length && typeof newPickOrder[nextPickIndex] === "string") {
        nextPickIndex++;
      }
    }

    // --- Create the final update object ---
    const updatedDraft = {
        ...draftState,
        teams: newTeams,
        availablePlayers: newAvailablePlayers,
        completedPicks: newCompletedPicks,
        currentPickIndex: nextPickIndex,
      pickOrder: newPickOrder,
        pickEndsAt: Date.now() + DRAFT_PICK_TIME_LIMIT_IN_MS,
    };

    const token = await currentUser?.getIdTokenResult(true);
    console.log(token?.claims);

    // --- Atomically write the entire update back to Firestore ---
    await updateDoc(draftDocRef, updatedDraft);

  }, [draftState, draftDocRef, canDraftNow, allPlayers, currentUser, division]);

  if (loadingAuth) {
    return <div>Verifying Access...</div>;
  }

  const latestTeams = draftState.teams && draftState.teams.length > 0 ? draftState.teams : teams;

  return (
    <DraftPageContainer>
      <DraftHeader>
        <Title>Live Player Draft</Title>
        {!isDraftComplete && (
          <DraftTimerContainer>
            <DraftTimerLabel>Time Remaining</DraftTimerLabel>
            <DraftTimerDisplay isLowTime={timeLeft < 300}>
              {formatTime(timeLeft)}
            </DraftTimerDisplay>
          </DraftTimerContainer>
        )}
      </DraftHeader>

      <DraftContent>
        <TeamsSection>
          {latestTeams.map(team => (
            <TeamCardContainer key={team.id} isPicking={team.id === currentTeamIdPicking}>
              <TeamHeader>{team.name}</TeamHeader>
              <PlayerList>
                {team.players!.map(p => (
                  <PlayerListItem key={p.id} isCaptain={isPlayerCaptain(p, division)}>
                    <PlayerInfoOnCard>
                      <PlayerNameOnCard
                          href={createOpGgUrl(p.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                      >
                          {p.name}
                      </PlayerNameOnCard>
                      <PlayerRolesOnCard>
                        {p.role}
                        {p.secondaryRoles && p.secondaryRoles.length > 0 ? ` (${p.secondaryRoles.join(', ')})` : ''}
                      </PlayerRolesOnCard>
                    </PlayerInfoOnCard>

                    <PlayerEloOnCard>Peak: {rankTierToShortName(p.peakRankTier)}{p.peakRankDivision === -1 ? "" : p.peakRankDivision}</PlayerEloOnCard>
                    {p.soloRankTier !== "N/A" && <PlayerEloOnCard>Solo: {rankTierToShortName(p.soloRankTier)}{p.soloRankDivision === -1 ? "" : p.soloRankDivision}</PlayerEloOnCard>}
                    {p.flexRankTier !== "N/A" && <PlayerEloOnCard>Flex: {rankTierToShortName(p.flexRankTier)}{p.flexRankDivision === -1 ? "" : p.flexRankDivision}</PlayerEloOnCard>}
                  </PlayerListItem>
                ))}
              </PlayerList>
            </TeamCardContainer>
          ))}
        </TeamsSection>
        
        <PlayerPool
          players={draftState.availablePlayers}
          onDraft={handleDraftPlayer}
          disabled={isSpectator || !canDraftNow || isDraftComplete} 
        />
      </DraftContent>

      <PickOrderDisplay
        pickOrder={activePickOrder}
        teams={latestTeams}
        players={allPlayers}
        currentPickIndex={draftState.currentPickIndex}
        completedPicks={draftState.completedPicks}
      />
    </DraftPageContainer>
  );
};

export default DraftPage;
